import { useEffect, useState } from "react";
import { useSnackbarContext } from "@inductiveautomation/ws-components";

import i18n from "../../../i18n/i18n";
import { OrganizationActionTypeForApi } from "../Organizations.types";
import { useOrganizationQueries } from "./useOrganizationQueries";
import { openWorkspace } from "../../../utilities/linking";

const successText = (type: OrganizationActionTypeForApi) => {
  return i18n.t(`organizations.actions.${type}.success_message`);
};

export const useOrganizationActions = () => {
  const { addSnackbar } = useSnackbarContext();
  const {
    requestMembership,
    resendMembershipRequest,
    cancelMembershipRequest,
    leaveOrganization,
    acceptInvite,
    // isLoading,
    isError,
    errors,
    isSuccess,
  } = useOrganizationQueries();
  const [lastRequest, setLastRequest] = useState<
    OrganizationActionTypeForApi | undefined
  >();
  const [displayedSuccess, setDisplayedSuccess] = useState(false);
  const [displayedError, setDisplayedError] = useState(false);

  const performAction = async (
    action: OrganizationActionTypeForApi | "launch_workspace" | undefined,
    selectedOrganizationIds: string[] | undefined
  ) => {
    if (!action || !selectedOrganizationIds) return;

    if (action === "launch_workspace") {
      if (selectedOrganizationIds.length > 0) {
        openWorkspace(selectedOrganizationIds[0]);
      }
      return;
    }

    await Promise.all(
      selectedOrganizationIds.map(async (organizationId) => {
        const payload = { orgIdPayload: { organizationId } };
        if (action === "request_to_join") {
          await requestMembership(payload);
        } else if (action === "resend_request") {
          await resendMembershipRequest(payload);
        } else if (action === "cancel_request") {
          await cancelMembershipRequest(payload);
        } else if (
          ["leave_organization", "leave_multiple_organizations"].includes(
            action
          )
        ) {
          await leaveOrganization(payload);
        } else if (action === "accept_invite") {
          await acceptInvite(payload);
        }
      })
    );

    setLastRequest(action);
  };

  useEffect(() => {
    if (isSuccess && lastRequest && !displayedSuccess) {
      addSnackbar(successText(lastRequest));
      setDisplayedSuccess(true);
    }

    if (!isSuccess) {
      setDisplayedSuccess(false);
    }
  }, [isSuccess, lastRequest, displayedSuccess, addSnackbar]);

  useEffect(() => {
    if (isError && !displayedError) {
      // TODO: Replace with a more helpful error
      console.error(errors);
      addSnackbar("An error occurred", { type: "error" });
      setDisplayedError(true);
    }

    if (!isError) {
      setDisplayedError(false);
    }
  }, [isError, errors, displayedError, addSnackbar]);

  return { performAction };
};
